import axios from "axios";
import * as Sentry from "@sentry/react";
import { getRestApiUrl } from "./backendOverride";

// ---------------------------------------------------------------------------
// Incidencias de proveedores de mensajería (360dialog / Meta).
//
// El backend expone la lista de incidencias ACTIVAS para que un operador pueda
// encender un aviso en el dashboard sin desplegar. Contrato (IV-007):
//
//   GET /config/incidents → { incidents: [{ id, provider, severity, message,
//                                           startedAt, active }] }
//
// Sin incidencias activas devuelve `{ incidents: [] }` (200, no 404).
//
// POR QUÉ UN CLIENTE PROPIO Y NO EL `API` COMPARTIDO (revisión de dashboard#192):
// el interceptor de respuesta de `src/axios.js` hace logout incondicional ante
// un `UNAUTHORIZED` — `localStorage.clear()` + `window.location.replace("/")`.
// Ese interceptor corre ANTES del catch de aquí, así que un 401 en esta llamada
// echaría al usuario de la aplicación aunque este módulo devolviese `[]`. Un
// aviso informativo no puede tener poder para cerrar sesiones: este cliente
// manda el token pero NO instala interceptor de respuesta.
//
// Los headers `sentry-trace`/`baggage` los sigue inyectando Sentry por
// `tracePropagationTargets` (observability/init.js), que es global al navegador
// y no depende de la instancia de axios.
//
// REGLA DURA: este módulo lo consume un banner montado en DashboardLayout, que
// envuelve TODA la app autenticada. Nunca puede lanzar. Ante cualquier fallo
// —endpoint aún no desplegado, 500, timeout, payload con otra forma— devuelve
// lista vacía y el dashboard sigue como si nada.
// ---------------------------------------------------------------------------

export const INCIDENTS_ENDPOINT = "/config/incidents";

/** Canal público por el que el backend anuncia los cambios de aviso. */
export const INCIDENTS_CHANNEL = "platform_status";
export const INCIDENTS_EVENT = "provider-incident";

// Sin token de `info` en DesignTokens: cae al branch por defecto del styled
// (fondo blanco, texto gris) sin necesidad de añadir color nuevo.
const SEVERITIES = ["error", "warning", "info"];
const SEVERITY_ORDER = { error: 0, warning: 1, info: 2 };
const PROVIDERS = ["360", "META"];

// Avisos automáticos (docs/specs/2026-10-27-cintillo-web-interruptor): los
// escribe provider-status-monitor con id `auto-*` (el backend solo deja escribir
// ese prefijo al token de servicio del monitor). Se pintan solo si
// REACT_APP_PROVIDER_INCIDENTS_AUTO_BANNER vale exactamente "true"; los manuales
// (cualquier otro id) se pintan siempre, como antes.
export const AUTO_INCIDENT_PREFIX = "auto-";
const AUTO_BANNER_ENV_KEY = "REACT_APP_PROVIDER_INCIDENTS_AUTO_BANNER";

// Literal completo a propósito: Vite solo reemplaza
// `process.env.REACT_APP_PROVIDER_INCIDENTS_AUTO_BANNER` (vite.config.js lo
// define siempre, '' si no está) y compila un `process.env` suelto a `{}`, con
// lo que el interruptor nunca podría encenderse en el navegador.
const readAutoBannerSwitch = () => process.env.REACT_APP_PROVIDER_INCIDENTS_AUTO_BANNER;

/**
 * true solo con el string "true" exacto. Sin argumento lee la variable de build
 * en el momento de la llamada; un objeto explícito gana sobre ella.
 */
export const isAutoIncidentsEnabled = (env) => {
  if (env === undefined) return readAutoBannerSwitch() === "true";
  if (env === null || typeof env !== "object") return false;
  return env[AUTO_BANNER_ENV_KEY] === "true";
};

const isAutoIncident = (incident) =>
  typeof incident?.id === "string" && incident.id.startsWith(AUTO_INCIDENT_PREFIX);

/** Manual siempre; automático solo con `autoEnabled === true` (falla cerrado). */
export const isVisibleIncident = (incident, autoEnabled) =>
  autoEnabled === true || !isAutoIncident(incident);

const resolveAutoEnabled = (options) => (
  options?.autoEnabled === undefined ? isAutoIncidentsEnabled() : options.autoEnabled === true
);

const incidentsApi = axios.create();

incidentsApi.interceptors.request.use((config) => {
  config.headers.Authorization = `Bearer ${localStorage.getItem("token")}`;
  config.baseURL = `${getRestApiUrl()}`;
  return config;
});
// A propósito: NINGÚN interceptor de respuesta. Ver la nota de arriba.

// Reportar cada fallo inundaría Sentry, pero una bandera de módulo apagaría la
// señal para el resto de la sesión: si el endpoint falla al cargar y se cae de
// verdad tres horas más tarde, nadie se enteraría. Ventana temporal en su lugar.
const FAILURE_REPORT_WINDOW_MS = 30 * 60 * 1000;
let lastFailureReportedAt = 0;

const reportFailure = (error) => {
  const now = Date.now();
  if (now - lastFailureReportedAt < FAILURE_REPORT_WINDOW_MS) return;
  lastFailureReportedAt = now;
  // `warning`, no `exception`: mientras el endpoint no esté desplegado el
  // 404 es el estado ESPERADO, y un error aquí no degrada al usuario.
  Sentry.captureMessage("providerIncidents.fetch.failed", {
    level: "warning",
    tags: { component: "IncidentBanner", op: "fetch" },
    extra: { httpCode: error?.response?.status },
  });
};

const isRenderable = (incident) =>
  incident
  && typeof incident.message === "string"
  && incident.message.trim() !== ""
  && incident.active !== false
  && PROVIDERS.includes(incident.provider);

const normalize = (incident) => ({
  id: incident.id || `${incident.provider}-${incident.startedAt || ""}`,
  provider: incident.provider,
  // Una severidad desconocida no debe dejar la barra sin estilo: cae a "warning".
  severity: SEVERITIES.includes(incident.severity) ? incident.severity : "warning",
  message: incident.message.trim(),
  startedAt: incident.startedAt || "",
});

const bySeverity = (a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity];

export const sortIncidents = (incidents) => [...incidents].sort(bySeverity);

/**
 * Aplica a la lista en memoria el aviso que llegó por Pusher: lo añade, lo
 * reemplaza si ya estaba, o lo quita si vino desactivado. Un aviso automático
 * con el interruptor apagado se ignora: la lista vuelve tal cual.
 */
export const applyIncidentEvent = (incidents, rawIncident, options) => {
  const id = rawIncident?.id;
  if (!id) return incidents;
  if (!isVisibleIncident(rawIncident, resolveAutoEnabled(options))) return incidents;

  const rest = incidents.filter((incident) => incident.id !== id);
  if (!isRenderable(rawIncident)) return sortIncidents(rest);

  return sortIncidents([...rest, normalize(rawIncident)]);
};

/**
 * Devuelve las incidencias activas, ya normalizadas y ordenadas por severidad
 * (primero las más graves), sin los automáticos si el interruptor está apagado.
 * Nunca lanza: ante cualquier fallo devuelve [].
 */
export const getProviderIncidents = async (options) => {
  try {
    const autoEnabled = resolveAutoEnabled(options);
    const { data } = await incidentsApi.get(INCIDENTS_ENDPOINT);
    const incidents = data?.incidents;
    if (!Array.isArray(incidents)) return [];
    const visible = incidents.filter(
      (incident) => isRenderable(incident) && isVisibleIncident(incident, autoEnabled)
    );
    return sortIncidents(visible.map(normalize));
  } catch (error) {
    reportFailure(error);
    return [];
  }
};

export default getProviderIncidents;
